import React, {useEffect, useState} from 'react';
import {Button, List, Input, Select, Tooltip} from 'antd';
import {
    MenuFoldOutlined,
    MenuUnfoldOutlined,
    EnvironmentOutlined,
    DashboardOutlined,
    ClockCircleOutlined, FileExcelFilled,
} from '@ant-design/icons';
import * as XLSX from 'xlsx';
import {useSelector} from "react-redux";

const {Search} = Input;
const {Option} = Select;

const MapSideBar = ({
                        vehicles,
                        onVehicleSelect,
                        searchTerm,
                        setSearchTerm,
                        selectedStatuses,
                        setSelectedStatuses,
                        selectedOnlineStatuses,
                        setSelectedOnlineStatuses,
                        selectedBrandType,
                        setSelectedBrandType,
                        selectedFuelType,
                        setSelectedFuelType,
                        selectedIgnitionType,
                        setSelectedIgnitionType,
                        selectedGroupTitle,
                        setSelectedGroupTitle
                    }) => {

    const {user} = useSelector((state) => state.auth);
    const [isOpen, setIsOpen] = useState(true);
    const [searchResults, setSearchResults] = useState(vehicles);
    const [groupTitles, setGroupTitles] = useState([])

    const toggleSidebar = () => {
        setIsOpen(!isOpen);
    };

    useEffect(() => {
        const uniqueGroupTitles = [...new Set(
            vehicles
                .map(vehicle => vehicle.groupTitle)
                .filter(title => title && title.trim() !== '')
        )];
        setGroupTitles(uniqueGroupTitles);
    }, [vehicles]);


    const handleSearch = (value) => {
        setSearchTerm(value);
    };

    const formatDate = (timestamp) => {
        const date = new Date(timestamp);
        date.setHours(date.getHours() + 3);
        return `${date.toLocaleDateString()} ${date.toLocaleTimeString()}`;
    };


    const getBackgroundColor = (status) => {
        switch (status) {
            case 'red':
                return 'rgba(255, 0, 0, 0.1)';
            case 'green':
                return 'rgba(0, 255, 0, 0.1)';
            case 'orange':
                return 'rgba(255, 165, 0, 0.1)';
            case 'gray':
                return 'rgba(128, 128, 128, 0.1)';
            case 'black':
                return 'rgba(0, 0, 0, 0.1)';
            default:
                return 'white';
        }
    };


    const handleStatusChange = (value) => {
        setSelectedStatuses(value);
    };

    const handleFuelStatusChange = (value) => {
        setSelectedFuelType(value);
    };

    const handleBrandStatusChange = (value) => {
        setSelectedBrandType(value);
    };

    const handleIgnitionStatusChange = (value) => {
        setSelectedIgnitionType(value);
    };

    const handleGroupTitleStatusChange = (value) => {
        setSelectedGroupTitle(value);
    };

    const handleOnlineStatusChange = (value) => {
        setSelectedOnlineStatuses(value);
    };


    const handleExcel = () => {
        const title = 'Canlı_Harita_Raporu';
        const workbook = XLSX.utils.book_new();

        const header = [
            ['Rapor Tipi:', 'Harita'],
            ['Rapor Oluşturma Tarihi:', new Date().toLocaleString()],
            ['Raporu Oluşturan:', user?.name],
            [''],
            [
                'Kapı No',
                'Marka',
                'Yakıt Türü',
                'Plaka',
                'Cihaz ID',
                'Şasi No',
                'Rölanti Durumu', // Rölanti başlığı eklendi
                'Lokasyon',
                'Hız',
                'Veri Tarihi'
            ]
        ];

        const body = searchResults.map(item => [
            item.operationNumber || '-',
            item.brand === 'mercedes' ? 'Mercedes' :
                item.brand === 'otokar' ? 'Otokar' : item.brand || '-',
            item.fuelType === 'cng' ? 'Gazlı' :
                item.fuelType === 'diesel' ? 'Dizel' : item.fuelType || '-',
            item.plateNumber || '-',
            item.vehicle_id || '-',
            item.chassisNumber || '-',
            item.cruiseStatus ? item.cruiseStatus : '-', // Rölanti verisi eklendi
            `Enlem: ${item.gps?.lat || 'Bilinmiyor'}, Boylam: ${item.gps?.lon || 'Bilinmiyor'}`,
            `${item.vehicleSpeed?.mean?.toFixed(2) || 0} km/h`,
            item.createdAt_last_data ? new Date(item.createdAt_last_data).toLocaleString() : 'Bilinmiyor'
        ]);

        const worksheetData = [...header, ...body];

        const worksheet = XLSX.utils.aoa_to_sheet(worksheetData);

        const maxWidths = worksheetData[0].map((_, colIndex) =>
            Math.max(...worksheetData.map(row => (row[colIndex]?.toString().length || 0))) + 2
        );

        worksheet['!cols'] = maxWidths.map(width => ({width}));

        XLSX.utils.book_append_sheet(workbook, worksheet, 'Canlı Harita Raporu');

        XLSX.writeFile(workbook, `${title}.xlsx`);
    };

    useEffect(() => {
        const results = vehicles.filter((item) => {
            const matchesSearchTerm =
                !searchTerm ||
                item.plateNumber?.toLowerCase().includes(searchTerm.toLowerCase()) ||
                item.operationNumber
                    ?.toLowerCase()
                    .includes(searchTerm.toLowerCase());

            const matchesStatus =
                !selectedStatuses ||
                selectedStatuses.length === 0 ||
                selectedStatuses.includes(item.status);

            const matchesOnlineStatus =
                !selectedOnlineStatuses ||
                selectedOnlineStatuses.length === 0 ||
                selectedOnlineStatuses.includes(item.onlineStatus);

            const matchesBrand =
                !selectedBrandType || selectedBrandType.length === 0 ||
                (item.brand && selectedBrandType.includes(item.brand));

            const matchesFuelType =
                !selectedFuelType ||
                selectedFuelType.length === 0 ||
                (item.fuelType && selectedFuelType.includes(item.fuelType));

            const matchesIgnitionType =
                !selectedIgnitionType ||
                selectedIgnitionType.length === 0 ||
                (item.cruiseStatus &&
                    selectedIgnitionType.includes(item.cruiseStatus));

            const matchesGroupTitle =
                !selectedGroupTitle || selectedGroupTitle.length === 0 ||
                selectedGroupTitle.includes(item.groupTitle);

            return (
                matchesSearchTerm &&
                matchesStatus &&
                matchesOnlineStatus &&
                matchesBrand &&
                matchesFuelType &&
                matchesIgnitionType &&
                matchesGroupTitle
            );
        });

        const sortedResults = results.sort((a, b) => {
            const aOp = a.operationNumber || '';
            const bOp = b.operationNumber || '';
            return aOp.localeCompare(bOp, undefined, {numeric: true});
        });

        setSearchResults(sortedResults);
    }, [
        vehicles,
        searchTerm,
        selectedStatuses,
        selectedOnlineStatuses,
        selectedFuelType,
        selectedBrandType,
        selectedIgnitionType,
        selectedGroupTitle,
    ]);


    return (
        <>
            <Button
                style={{
                    position: 'absolute',
                    height: 100,
                    borderTopRightRadius: 60,
                    borderBottomRightRadius: 60,
                    top: '45%',
                    left: isOpen ? 455 : 2,
                    zIndex: 1000,
                    backgroundColor: 'white',
                    transform: isOpen ? 'none' : 'rotate(360deg)',
                    transition: 'left 0.5s',
                }}
                icon={isOpen ? <MenuFoldOutlined/> : <MenuUnfoldOutlined/>}
                onClick={toggleSidebar}
            />
            <div className={`sidebar ${isOpen ? 'open' : 'closed'}`}>
                <div style={{padding: 10, height: '100%', display: 'flex', flexDirection: 'column'}}>
                    <div style={{
                        marginBottom: 10,
                        display: 'flex',
                        justifyContent: 'space-between',
                        alignItems: 'center'
                    }}>
                        <div style={{flex: 10}}>
                            <Search
                                placeholder="Plaka, kapı numarasına göre araç Ara"
                                enterButton
                                onSearch={handleSearch}
                                value={searchTerm}
                                onChange={(e) => setSearchTerm(e.target.value)}
                                allowClear
                                style={{width: '100%'}}
                            />
                        </div>
                        <div style={{flex: 1, textAlign: 'right', marginLeft: 10}}>
                            <Tooltip title="Rapor olarak indir">
                                <Button
                                    type="primary"
                                    icon={<FileExcelFilled/>}
                                    onClick={handleExcel}
                                />
                            </Tooltip>
                        </div>
                    </div>
                    <div style={{marginBottom: 15, flexShrink: 0, display: 'flex', justifyContent: 'space-between'}}>
                        <Select
                            mode="multiple"
                            placeholder="Duruma göre filtrele"
                            style={{width: 'calc(50% - 4px)'}}
                            onChange={handleStatusChange}
                            allowClear
                            value={selectedStatuses}
                        >
                            <Option value="red">Arızalı araçlar</Option>
                            <Option value="orange">Uyarılı araçlar</Option>
                            <Option value="green">Hatasız aktif araçlar</Option>
                            <Option value="gray">Hatasız inaktif araçlar</Option>
                            <Option value="black">Veri olmayan araçlar</Option>
                        </Select>
                        <Select
                            mode="multiple"
                            placeholder="Çevrimiçi Duruma göre filtrele"
                            style={{width: 'calc(50% - 4px)'}}
                            onChange={handleOnlineStatusChange}
                            allowClear
                            value={selectedOnlineStatuses}
                        >
                            <Option value="online">Online</Option>
                            <Option value="offline">Offline</Option>
                        </Select>
                    </div>
                    <div style={{marginBottom: 15, flexShrink: 0, display: 'flex', justifyContent: 'space-between'}}>
                        <Select
                            mode="multiple"
                            placeholder="Yakıt türüne göre filtrele"
                            style={{width: 'calc(50% - 4px)'}}
                            onChange={handleFuelStatusChange}
                            allowClear
                            value={selectedFuelType}
                        >
                            <Option value="cng">Gazlı</Option>
                            <Option value="diesel">Dizel</Option>
                        </Select>
                        <Select
                            mode="multiple"
                            placeholder="Markaya göre filtrele"
                            style={{width: 'calc(50% - 4px)'}}
                            onChange={handleBrandStatusChange}
                            allowClear
                            value={selectedBrandType}
                        >
                            <Option value="mercedes">Mercedes</Option>
                            <Option value="otokar">Otokar</Option>
                        </Select>
                    </div>
                    <div style={{marginBottom: 15, flexShrink: 0, display: 'flex', justifyContent: 'space-between'}}>
                        <Select
                            mode="multiple"
                            placeholder="Araç grubuna göre filtrele"
                            style={{width: 'calc(50% - 4px)'}}
                            onChange={handleGroupTitleStatusChange}
                            allowClear
                            value={selectedGroupTitle}
                        >
                            {groupTitles.map(group => (
                                <Option key={group} value={group}>{group}</Option>
                            ))}
                        </Select>
                        <Select
                            mode="multiple"
                            placeholder="Rölanti durumuna göre filtrele"
                            style={{width: 'calc(50% - 4px)'}}
                            onChange={handleIgnitionStatusChange}
                            allowClear
                            value={selectedIgnitionType}
                        >
                            <Option value='Seyir Halinde'>Seyir Halinde</Option>
                            <Option value='Rölanti'>Rölanti</Option>
                            <Option value='Park'>Park</Option>
                            <Option value='Durma'>Durma</Option>
                        </Select>
                    </div>
                    <div style={{overflowY: 'auto', flexGrow: 1}}>
                        <List
                            dataSource={searchResults}
                            renderItem={(item) => (
                                <List.Item
                                    key={item.vehicle_id}
                                    className="vehicle-list-item"
                                    onClick={() => {
                                        if (item.gps && typeof item.gps.lat !== 'undefined' && typeof item.gps.lon !== 'undefined') {
                                            onVehicleSelect(item.gps.lat, item.gps.lon, 22);
                                        }
                                    }}
                                    style={{
                                        backgroundColor: getBackgroundColor(item.status),
                                        padding: 8,
                                        marginBottom: 5,
                                    }}
                                >
                                    <div className="info">
                                        <div style={{marginBottom: 5, fontSize: 13}}>
                                            <strong>Kapı No:</strong> {item.operationNumber}
                                        </div>
                                        <div style={{marginBottom: 5, fontSize: 13}}>
                                            <strong>Marka : </strong>
                                            {item.brand === 'otokar' ? 'Otokar' :
                                                item.brand === 'mercedes' ? 'Mercedes' :
                                                    item.brand || '-'}
                                        </div>
                                        <div style={{marginBottom: 5, fontSize: 13}}>
                                            <strong>Yakıt Türü : </strong>
                                            {item.fuelType === 'cng' ? 'Gazlı' :
                                                item.fuelType === 'diesel' ? 'Dizel' :
                                                    item.fuelType || '-'}
                                        </div>
                                        <div style={{marginBottom: 5, fontSize: 13}}>
                                            <strong>Plaka:</strong> {item.plateNumber ? item.plateNumber : '-'}
                                        </div>
                                        <div style={{marginBottom: 5, fontSize: 13}}>
                                            <strong>Cihaz ID:</strong> {item.vehicle_id ? item.vehicle_id : '-'}
                                        </div>
                                        <div style={{marginBottom: 5, fontSize: 13}}>
                                            <strong>Şasi No:</strong> {item.chassisNumber ? item.chassisNumber : '-'}
                                        </div>
                                        <div style={{marginBottom: 5, fontSize: 13}}>
                                            <strong>Rölanti Durumu
                                                :</strong> {item.cruiseStatus ? item.cruiseStatus : '-'}
                                        </div>
                                        <div style={{marginBottom: 5, fontSize: 13}}>
                                            <strong>Araç Grubu
                                                :</strong> {item.groupTitle ? item.groupTitle : '-'}
                                        </div>
                                        <div style={{display: 'flex', alignItems: 'center', marginBottom: 5}}>
                                            <EnvironmentOutlined style={{marginRight: 5}}/>
                                            <span style={{fontSize: 12}}>
                                                {item.gps?.lat ? `${item.gps?.lat}, ${item.gps?.lon}` : 'Bilinmiyor'}
                                              </span>
                                            <DashboardOutlined style={{marginRight: 5, marginLeft: 10}}/>
                                            <span style={{fontSize: 12}}>
                                                {item.wheelBasedSpeed?.mean
                                                    ? `${item.wheelBasedSpeed.mean.toFixed(2)} km/h`
                                                    : `0 km/h`}
                                              </span>
                                            <ClockCircleOutlined style={{marginRight: 5, marginLeft: 10}}/>
                                            <span style={{fontSize: 12}}>
                                                {item.createdAt_last_data ? formatDate(item.createdAt_last_data) : 'Bilinmiyor'}
                                              </span>
                                        </div>
                                    </div>
                                </List.Item>
                            )}
                        />
                    </div>
                </div>
            </div>
        </>
    );
};

export default MapSideBar;
